import { useEffect, useMemo, useRef, useState } from 'react';

import { RadioButtonComponent } from '@syncfusion/ej2-react-buttons';
import { GridComponent, Inject, Sort, Toolbar, Page, RecordClickEventArgs, Filter } from '@syncfusion/ej2-react-grids';
import { ItemDirective, ItemsDirective, ToolbarComponent } from '@syncfusion/ej2-react-navigations';

import { useToast } from '@hooks';
import { requests } from 'common/axios';
import { DropDownData } from 'components/Common/SyncfusionWrappers/Syncfusion';
import { IpaAdminTabId } from 'components/ProviderData/IpaAdministration/atoms/enums';
import {
    GetAllPreferredProvidersResponse,
    GetIpaNetworkMappingsDistinctTinsResponse,
    IPADetail,
    IPreferredStatusMapping,
    PreferredStatusMappingSubmitMode,
} from 'components/ProviderData/IpaAdministration/atoms/Models';
import styles from 'components/ProviderData/IpaAdministration/organisms/PreferredStatus/PreferredStatus.module.scss';
import {
    BASE_URL,
    PreferredStatusApi,
} from 'components/ProviderData/IpaAdministration/organisms/PreferredStatus/PreferredStatusApi';
import PreferredStatusForm from 'components/ProviderData/IpaAdministration/organisms/PreferredStatus/PreferredStatusForm';
import {
    getPreferredStatusFormButtons,
    getPreferredStatusGridColumns,
} from 'components/ProviderData/IpaAdministration/organisms/PreferredStatus/PreferredStatusGrid.metadata';
import {
    moveToBottom,
    normalizePreferredStatusDates,
} from 'components/ProviderData/IpaAdministration/organisms/PreferredStatus/utils';
import { selectedTabIdService } from 'components/ProviderData/subjects/IpaAdministrationService';
import { IpaPreferredStatusService } from 'components/ProviderData/subjects/IpaPreferredStatusService';
import { Dialog } from 'interfaces/Dialog';
import { dialogService } from 'subjects/common/DialogService';

type PreferredStatusGridProps = {
    isReadOnly: boolean;
    ipa: IPADetail;
};

const PreferredStatusGrid = ({
    isReadOnly,
    ipa: { id: ipaId, effDate, endDate, ipaCode },
}: PreferredStatusGridProps) => {
    const [dataSource, setDataSource] = useState<IPreferredStatusMapping[]>([]);
    const [toggleOption, setToggleOption] = useState<'All' | 'Active'>('All');
    const [tinDropdownData, setTinDropdownData] = useState<DropDownData[]>([]);
    const [statusCodeDropdownData, setStatusCodeDropdownData] = useState<DropDownData[]>([]);
    const { showErrorToast } = useToast();
    const gridRef = useRef<GridComponent>(null);

    const columns = useMemo(() => getPreferredStatusGridColumns(statusCodeDropdownData), [statusCodeDropdownData]);

    const getViewModeRadioButton = (option: 'All' | 'Active') => (
        <RadioButtonComponent
            label={option}
            value={option}
            checked={toggleOption === option}
            onClick={() => setToggleOption(option)}
        />
    );

    const ToggleTemplate = () => (
        <div className='toolbar-filter-by-container'>
            {getViewModeRadioButton('All')}
            &emsp;&emsp;&emsp;
            {getViewModeRadioButton('Active')}
        </div>
    );

    const displayForm = (mode: PreferredStatusMappingSubmitMode, data: IPreferredStatusMapping) => {
        const dialog: Dialog = {};
        dialog.header = mode === 'create' ? 'Creating new Preferred Status record' : 'Updating Preferred Status record';
        dialog.content = () => (
            <PreferredStatusForm
                ipaStartDate={new Date(effDate)}
                ipaEndDate={endDate ? new Date(endDate) : new Date('9999-12-31')}
                data={data}
                tinSource={tinDropdownData}
                statusCodeSource={statusCodeDropdownData}
                isReadOnly={isReadOnly}
            />
        );
        dialog.buttons = getPreferredStatusFormButtons({ isReadOnly, mode });
        dialog.width = 800;
        dialog.height = 350;
        dialogService.open(dialog);
    };

    const updateDataSource = () => {
        if (!ipaId) return;

        const endpointUrl = `${BASE_URL}/GetAllIpaWebPreferredStatus?ipaId=${ipaId}&toggleOption=${toggleOption}&activeDate=${new Date().toISOString()}`;

        requests.get<IPreferredStatusMapping[]>(endpointUrl).then((data: IPreferredStatusMapping[]) => {
            const parsed = normalizePreferredStatusDates(data);
            setDataSource(parsed);
        });
    };

    const handleTaxIdsResult = (tinsResult: PromiseSettledResult<GetIpaNetworkMappingsDistinctTinsResponse>) => {
        if (tinsResult.status === 'fulfilled') {
            const tins = tinsResult.value.data.map((number) => ({ id: number, desc: number }));
            setTinDropdownData(tins);
        } else {
            showErrorToast('Failed to load Preferred Status TINs.');
        }
    };

    const handleStatusCodesResult = (statusCodesResult: PromiseSettledResult<GetAllPreferredProvidersResponse>) => {
        if (statusCodesResult.status === 'fulfilled') {
            const statusCodes = statusCodesResult.value.data.map((code) => ({
                id: code.parameterStatus,
                desc: code.preferredStatusDescription,
            }));

            setStatusCodeDropdownData(
                moveToBottom(statusCodes, (item) => item.desc.toLocaleLowerCase() === 'participating'),
            );
        } else {
            showErrorToast('Failed to load Preferred Status codes.');
        }
    };

    useEffect(() => {
        if (!ipaId) return;
        const subscription = IpaPreferredStatusService.refreshGrid$(ipaId).subscribe(() => {
            updateDataSource();
        });
        return () => subscription.unsubscribe();
    }, [ipaId]);

    useEffect(() => updateDataSource(), [ipaId, toggleOption]);

    useEffect(() => {
        const subscription = selectedTabIdService.get$(ipaId).subscribe(({ tabId: selectedTabId }) => {
            if (selectedTabId !== IpaAdminTabId.PreferredStatus) return;

            Promise.allSettled([PreferredStatusApi.getTaxIdsByIpaCode(ipaCode)]).then((results) => {
                handleTaxIdsResult(results[0]);
            });
        });
        return () => subscription.unsubscribe();
    }, [ipaId]);

    useEffect(() => {
        Promise.allSettled([
            PreferredStatusApi.getTaxIdsByIpaCode(ipaCode),
            PreferredStatusApi.getPreferredStatusCodes(),
        ]).then((results) => {
            handleTaxIdsResult(results[0]);
            handleStatusCodesResult(results[1]);
        });
    }, []);

    return (
        <div className='grid-container'>
            <ToolbarComponent className={styles.toolbar}>
                <ItemsDirective>
                    <ItemDirective
                        prefixIcon='e-plus'
                        text='Create'
                        click={() =>
                            displayForm('create', {
                                ipaId,
                                startDate: new Date(effDate),
                                endDate: endDate ? new Date(endDate) : new Date('9999-12-31'),
                            } as IPreferredStatusMapping)
                        }
                    />
                    <ItemDirective
                        prefixIcon='e-refresh'
                        text='Refresh'
                        click={updateDataSource}
                    />

                    <ItemDirective
                        align='Right'
                        template={ToggleTemplate}
                    />
                </ItemsDirective>
            </ToolbarComponent>
            <GridComponent
                id='ipaPreferredStatusGrid'
                ref={gridRef}
                pageSettings={{ pageSize: 25 }}
                sortSettings={{ columns: [{ field: 'createdAt', direction: 'Descending' }] }}
                height={450}
                allowPaging
                allowSorting
                allowFiltering={true}
                filterSettings={{
                    type: 'Excel',
                }}
                columns={columns}
                dataSource={dataSource}
                dataBound={() => gridRef.current?.autoFitColumns()}
                recordClick={(args: RecordClickEventArgs) => {
                    displayForm('edit', args.rowData as IPreferredStatusMapping);
                }}
                actionFailure={(args) => {
                    console.error(args);
                    showErrorToast('Something went wrong while retrieving Preferred Status data.');
                }}
            >
                <Inject services={[Sort, Toolbar, Page, Filter]} />
            </GridComponent>
        </div>
    );
};

export default PreferredStatusGrid;
